import React, { useState, useEffect, useCallback, useRef } from 'react'
import { API_BASE_URL } from '../../config/api'
import { authFetch } from '../../utils/authFetch'
import {
  Globe,
  Upload,
  RefreshCw,
  Trash2,
  ChevronDown,
  ChevronUp,
  FileText,
  AlertCircle,
  Loader2,
  ExternalLink,
} from 'lucide-react'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CrawledPage {
  id: string
  tenant_id: string
  source_url: string | null
  filename: string | null
  source_type: string
  status: 'pending' | 'processing' | 'success' | 'failed'
  chunk_count: number
  content_hash: string | null
  extracted_text_preview: string | null
  error_message: string | null
  crawled_at: string | null
  created_at: string
  updated_at: string | null
}

interface CrawlManagementProps {
  tenantId: string
}

// ---------------------------------------------------------------------------
// Status badge colours
// ---------------------------------------------------------------------------

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  processing: 'bg-blue-100 text-blue-800',
  success: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const CrawlManagement: React.FC<CrawlManagementProps> = ({ tenantId }) => {
  // State
  const [pages, setPages] = useState<CrawledPage[]>([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [currentPage, setCurrentPage] = useState(0) // offset-based
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [previews, setPreviews] = useState<Record<string, string>>({})
  const [urlInput, setUrlInput] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [uploadDragActive, setUploadDragActive] = useState(false)
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const PAGE_SIZE = 50

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const fetchPages = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        sort_by: sortBy,
        sort_order: sortOrder,
        limit: String(PAGE_SIZE),
        offset: String(currentPage),
      })
      if (statusFilter) params.set('status', statusFilter)

      const res = await authFetch(
        `${API_BASE_URL}/api/admin/crawl/pages?${params.toString()}`
      )
      const data = await res.json()
      if (data.pages) {
        setPages(data.pages)
        setTotal(data.total ?? 0)
      }
    } catch (err) {
      console.error('Failed to fetch crawled pages:', err)
    } finally {
      setLoading(false)
    }
  }, [sortBy, sortOrder, currentPage, statusFilter])

  useEffect(() => {
    fetchPages()
  }, [fetchPages])

  // ---------------------------------------------------------------------------
  // URL submission
  // ---------------------------------------------------------------------------

  const handleSubmitUrl = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!urlInput.trim() || isSubmitting) return
    setIsSubmitting(true)
    try {
      await authFetch(`${API_BASE_URL}/api/admin/crawl/url`, {
        method: 'POST',
        body: JSON.stringify({ url: urlInput.trim(), follow_links: true }),
      })
      setUrlInput('')
      fetchPages()
    } catch (err) {
      console.error('Failed to submit URL:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  // ---------------------------------------------------------------------------
  // File upload
  // ---------------------------------------------------------------------------

  const uploadFile = async (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    setUploadingFiles((prev) => [...prev, file.name])
    try {
      await authFetch(`${API_BASE_URL}/api/admin/crawl/upload`, {
        method: 'POST',
        body: formData,
      })
      fetchPages()
    } catch (err) {
      console.error(`Failed to upload ${file.name}:`, err)
    } finally {
      setUploadingFiles((prev) => prev.filter((n) => n !== file.name))
    }
  }

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setUploadDragActive(false)
    const files = Array.from(e.dataTransfer.files)
    files.forEach(uploadFile)
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return
    Array.from(e.target.files).forEach(uploadFile)
    e.target.value = ''
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const handleRecrawl = async (pageId: string) => {
    try {
      await authFetch(`${API_BASE_URL}/api/admin/crawl/pages/${pageId}/recrawl`, {
        method: 'POST',
      })
      fetchPages()
    } catch (err) {
      console.error('Recrawl failed:', err)
    }
  }

  const handleDelete = async (pageId: string) => {
    if (!window.confirm('Delete this page and its vectors?')) return
    try {
      await authFetch(`${API_BASE_URL}/api/admin/crawl/pages/${pageId}`, {
        method: 'DELETE',
      })
      fetchPages()
    } catch (err) {
      console.error('Delete failed:', err)
    }
  }

  const handleDeleteDomain = async () => {
    if (pages.length === 0) return
    const firstUrl = pages[0].source_url
    if (!firstUrl) return
    try {
      const domain = new URL(firstUrl).hostname
      if (!window.confirm(`Delete ALL pages for domain ${domain}?`)) return
      await authFetch(`${API_BASE_URL}/api/admin/crawl/domain/${domain}`, {
        method: 'DELETE',
      })
      fetchPages()
    } catch (err) {
      console.error('Domain delete failed:', err)
    }
  }

  // ---------------------------------------------------------------------------
  // Row expansion / preview
  // ---------------------------------------------------------------------------

  const toggleRow = async (pageId: string) => {
    const next = new Set(expandedRows)
    if (next.has(pageId)) {
      next.delete(pageId)
    } else {
      next.add(pageId)
      if (!previews[pageId]) {
        try {
          const res = await authFetch(
            `${API_BASE_URL}/api/admin/crawl/pages/${pageId}/preview`
          )
          const data = await res.json()
          setPreviews((prev) => ({ ...prev, [pageId]: data.preview || 'No preview available.' }))
        } catch {
          setPreviews((prev) => ({ ...prev, [pageId]: 'Failed to load preview.' }))
        }
      }
    }
    setExpandedRows(next)
  }

  // ---------------------------------------------------------------------------
  // Sorting helper
  // ---------------------------------------------------------------------------

  const toggleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(column)
      setSortOrder('desc')
    }
    setCurrentPage(0)
  }

  const SortIcon: React.FC<{ column: string }> = ({ column }) => {
    if (sortBy !== column) return null
    return sortOrder === 'asc' ? (
      <ChevronUp className="inline w-4 h-4 ml-1" />
    ) : (
      <ChevronDown className="inline w-4 h-4 ml-1" />
    )
  }

  // ---------------------------------------------------------------------------
  // Pagination
  // ---------------------------------------------------------------------------

  const showingFrom = total === 0 ? 0 : currentPage + 1
  const showingTo = Math.min(currentPage + PAGE_SIZE, total)

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Content Management</h1>
        <p className="text-gray-600">
          Crawl URLs or upload documents to make your content searchable by guests.
        </p>
      </div>

      {/* URL Submission Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
          <Globe className="w-5 h-5 mr-2 text-purple-600" />
          Crawl a URL
        </h2>
        <form onSubmit={handleSubmitUrl} className="flex gap-3">
          <input
            type="url"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="https://example.com/page"
            className="flex-1 border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            required
          />
          <button
            type="submit"
            disabled={isSubmitting || !urlInput.trim()}
            className="inline-flex items-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Globe className="w-4 h-4 mr-2" />
            )}
            Crawl
          </button>
        </form>
      </div>

      {/* File Upload Zone */}
      <div
        className={`bg-white rounded-lg shadow-sm border-2 border-dashed p-8 text-center transition-colors ${
          uploadDragActive
            ? 'border-purple-500 bg-purple-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragOver={(e) => {
          e.preventDefault()
          setUploadDragActive(true)
        }}
        onDragLeave={() => setUploadDragActive(false)}
        onDrop={handleFileDrop}
      >
        <Upload className="w-10 h-10 mx-auto text-gray-400 mb-3" />
        <p className="text-sm text-gray-600 mb-1">
          Drag & drop files here, or{' '}
          <button
            type="button"
            className="text-purple-600 underline hover:text-purple-700"
            onClick={() => fileInputRef.current?.click()}
          >
            browse
          </button>
        </p>
        <p className="text-xs text-gray-400">Accepts PDF, DOCX, TXT, HTML</p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx,.txt,.html"
          multiple
          className="hidden"
          onChange={handleFileSelect}
        />
        {uploadingFiles.length > 0 && (
          <div className="mt-4 space-y-1">
            {uploadingFiles.map((name) => (
              <div
                key={name}
                className="text-xs text-gray-500 flex items-center justify-center gap-2"
              >
                <Loader2 className="w-3 h-3 animate-spin" />
                Uploading {name}...
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={statusFilter || ''}
          onChange={(e) => {
            setStatusFilter(e.target.value || null)
            setCurrentPage(0)
          }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="processing">Processing</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
        </select>

        <select
          value={sortBy}
          onChange={(e) => {
            setSortBy(e.target.value)
            setCurrentPage(0)
          }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="created_at">Created Date</option>
          <option value="crawled_at">Last Crawled</option>
          <option value="status">Status</option>
        </select>

        <button
          onClick={() => setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm hover:bg-gray-50"
        >
          {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
        </button>

        <button
          onClick={fetchPages}
          className="ml-auto inline-flex items-center text-sm text-purple-600 hover:text-purple-700"
        >
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </button>
      </div>

      {/* Pages Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading pages...
          </div>
        ) : pages.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No crawled content yet.</p>
            <p className="text-xs">Submit a URL or upload a file to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none"
                    onClick={() => toggleSort('source_url')}
                  >
                    URL / Filename <SortIcon column="source_url" />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Type
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none"
                    onClick={() => toggleSort('status')}
                  >
                    Status <SortIcon column="status" />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Chunks
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none"
                    onClick={() => toggleSort('crawled_at')}
                  >
                    Last Crawled <SortIcon column="crawled_at" />
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pages.map((page) => (
                  <React.Fragment key={page.id}>
                    <tr
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => toggleRow(page.id)}
                    >
                      <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">
                        <div className="flex items-center gap-2">
                          {page.source_type === 'url' ? (
                            <Globe className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          ) : (
                            <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          )}
                          <span className="truncate">
                            {page.source_url || page.filename || 'Unknown'}
                          </span>
                          {page.source_url && (
                            <a
                              href={page.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex-shrink-0"
                            >
                              <ExternalLink className="w-3 h-3 text-gray-400 hover:text-purple-600" />
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 capitalize">
                        {page.source_type}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                            STATUS_STYLES[page.status] || 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {page.status}
                        </span>
                        {page.error_message && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-red-600">
                            <AlertCircle className="w-3 h-3" />
                            <span className="truncate max-w-[200px]">{page.error_message}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {page.chunk_count ?? 0}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {page.crawled_at
                          ? new Date(page.crawled_at).toLocaleDateString()
                          : '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {page.source_type === 'url' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleRecrawl(page.id)
                              }}
                              className="p-1.5 text-gray-400 hover:text-purple-600 rounded hover:bg-purple-50"
                              title="Re-crawl"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDelete(page.id)
                            }}
                            className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedRows.has(page.id) && (
                      <tr>
                        <td colSpan={6} className="px-4 py-3 bg-gray-50">
                          <div className="text-sm text-gray-700 whitespace-pre-wrap max-h-48 overflow-y-auto rounded bg-gray-100 p-3">
                            {previews[page.id] ?? 'Loading preview...'}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>
            Showing {showingFrom}-{showingTo} of {total}
          </span>
          <div className="flex gap-2">
            <button
              disabled={currentPage === 0}
              onClick={() => setCurrentPage((p) => Math.max(0, p - PAGE_SIZE))}
              className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
            >
              Previous
            </button>
            <button
              disabled={currentPage + PAGE_SIZE >= total}
              onClick={() => setCurrentPage((p) => p + PAGE_SIZE)}
              className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Bulk Actions */}
      {pages.length > 0 && (
        <div className="flex gap-3">
          <button
            onClick={handleDeleteDomain}
            className="inline-flex items-center px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete All for Domain
          </button>
        </div>
      )}
    </div>
  )
}

export default CrawlManagement
